import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import PrepareTrackingDataHelper from '@Services/PrepareTrackingDataHelper';
import { citationLiteratureFormatter, patentFamilyFormatter } from '@Services/FormatUtils';
import externalUrl from '@Utils/ExternalUrl';
import DocumentButtons from '@Components/Results/documents/DocumentButtons';
import ListOfAuthors from '@Components/Results/reactions/reaction-details/ListOfAuthors';
import DocumentCollapsibleContent from '@Components/Results/documents/DocumentCollapsibleContent';
import DocumentJournals from '@Components/Results/documents/DocumentJournals';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { DOCUMENT_PROCESSING_TYPES } from '@Containers/ResultsFilters/ResultsFiltersConstants';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getDocumentNoAuthorLabel } from '@Components/Results/reactions/reaction-details/ReactionCitations';
import Catalyst from '@Services/Catalyst';
import { sectionsList, getFirstAvailableSection } from '@Components/Results/documents/DocumentExpandSections';

import './Document.scss';
import { Button } from '@els/els-react--button';

function Document(props) {
  const { metadata, document: citation, zoom: zoomValue, context } = props;
  const [state, setState] = useState(() => {
    const expandFlags = sectionsList.reduce((acc, flag) => {
      acc[flag] = false;
      return acc;
    }, {});
    return { updatedCitedByUrl: '', ...expandFlags };
  });

  useEffect(() => {
    async function updateUrls() {
      if (citation.issueCitedByCounter && citation.issueCitedByCounter > 0) {
        const updatedCitedByUrl = await Catalyst.appendVisitorIDToUrl(externalUrl(citation.citedBy));
        setState((prev) => ({ ...prev, updatedCitedByUrl }));
      }
    }
    updateUrls();
  }, [citation.issueCitedByCounter, citation.citedBy]);

  const extractExpandFlagsFromState = useCallback(
    () =>
      sectionsList.reduce((acc, flag) => {
        acc[flag] = state[flag];
        return acc;
      }, {}),
    [state]
  );

  const isPatent = useCallback(() => /patent/gi.test(getValueByNs(props, 'document.type')), [props]);

  const getTrackingAttrs = useCallback(
    (trackingAttrs, prepare = true) => ({
      'data-click-delegation': 'true',
      'data-tracking': 'true',
      'data-tracking-context': context || get(metadata, 'context'),
      'data-tracking-preview': getValueByNs(props, 'metadata.isViewPreview'),
      'data-tracking-track-link-location-in-results': true,
      'data-tracking-from-rt': metadata.fromRT,
      ...trackingAttrs,
      ...(prepare ? PrepareTrackingDataHelper.prepare(citation) : {}),
    }),
    [context, metadata, citation, props]
  );

  const toggleSection = useCallback((event, section) => {
    if (event) {
      const target = event.currentTarget;
      event.preventDefault();
      setTimeout(() => {
        if (!Document.checkIfClickTargetContainsSelection(target)) {
          setState((prev) => ({ ...prev, [section]: !prev[section] }));
        }
      }, 0);
    } else {
      setState((prev) => ({ ...prev, [section]: !prev[section] }));
    }
  }, []);

  const titleClickToggleSection = useCallback(
    (e) => {
      const section = getFirstAvailableSection(citation);
      if (section) {
        toggleSection(e, section);
      }
    },
    [citation, toggleSection]
  );

  const { abstractExpanded } = state;
  const { updatedCitedByUrl } = state;
  const zoom = metadata.isViewPreview ? 0 : zoomValue;
  const processingMode =
    Auth.isFeatureEnabled(FEATURES.enableRccDocumentProcessedTypeLabel) && DOCUMENT_PROCESSING_TYPES.getType(citation.processingMode);

  return (
    <div className="u-els-display-flex u-els-margin-left u-els-font-size-meta document-content-container">
      <div className="document-content u-els-display-flex u-els-padding-top">
        <div className="document-element">
          <Button
            type="link"
            className="e2e-document-action-title u-els-display-block"
            tabIndex={citation.abstractText ? '0' : '-1'}
            aria-label={`Toggle abstract for ${citation.title}`}
            onClick={titleClickToggleSection}
            {...getTrackingAttrs({
              'data-tracking': !abstractExpanded,
              'data-tracking-type': 'contentlinkclick',
              'data-tracking-action': 'title',
            })}>
            {citation.title ? (
              <InnerHTML tag="h2" className="u-els-font-size-h4 u-els-color-text" html={citation.title} />
            ) : (
              <span className="u-els-font-size-h4 u-els-color-text">No title</span>
            )}
          </Button>
          <div className="u-els-font-size-meta">
            {isPatent() && citation.assignees && (
              <>
                {!Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) && <span>Current Patent Assignee: </span>}
                <InnerHTML tag="span" className="rx-element-assignee" html={citation.assignees + (citation.authors ? '; ' : '')} />
              </>
            )}
            {(() => {
              let authorsContent;
              if (citation.authors) {
                authorsContent = <ListOfAuthors metadata={metadata} citation={citation} />;
              } else if (citation.assignees) {
                authorsContent = null;
              } else {
                authorsContent = getDocumentNoAuthorLabel(citation.year);
              }
              return <span className="rx-element-authors">{authorsContent}</span>;
            })()}{' '}
            {/* for patent */}
            {isPatent() && (
              <InnerHTML
                tag="span"
                className="rx-element-literature"
                html={`- ${Document.literatureFormatter(citation.citationPatents)}`}
              />
            )}
          </div>
          {!isEmpty(citation.citationJournals) && <DocumentJournals journals={citation.citationJournals} doi={citation.doi} withDoiLink className="u-els-margin-top-1o2" />}
          {/* again for patent */}
          {isPatent() && citation.patentFamily && (
            <div className="u-els-font-size-meta">
              <span className="rx-element-literature-title">Patent Family Members: </span>
              <InnerHTML tag="span" html={Document.patentFormatter(citation.patentFamily)} />
            </div>
          )}
          <DocumentButtons
            document={citation}
            toggleSection={toggleSection}
            {...extractExpandFlagsFromState()}
            getTrackingAttrs={getTrackingAttrs}
            metadata={metadata}
          />
        </div>

        {(citation.issueCitedByCounter > 0 || citation.processingMode) && (
          <div className="u-els-margin-left u-els-margin-right">
            {processingMode && (
              <div className="rx-element-processing-mode e2e-processing-mode">
                <svg className="els-btn-icon rx-border-none els-btn-icon-sm processing-mode-icon">
                  <use xlinkHref={`#icon-${processingMode.icon}`} />
                </svg>
                {processingMode.label}
              </div>
            )}
            {citation.issueCitedByCounter > 0 && (
              <a
                className="els-btn els-btn-link blue-compact--underline els-btn-xs e2e-cited-count rx-element-cited-count"
                target="_blank"
                href={updatedCitedByUrl}
                {...getTrackingAttrs(
                  {
                    'data-tracking-type': 'linkout',
                    'data-tracking-action': 'cited',
                  },
                  true
                )}>
                Cited {citation.issueCitedByCounter} times
              </a>
            )}
          </div>
        )}
      </div>

      <DocumentCollapsibleContent
        metadata={metadata}
        document={citation}
        zoom={zoom}
        toggleSection={toggleSection}
        {...extractExpandFlagsFromState()}
        getTrackingAttrs={getTrackingAttrs}
      />
    </div>
  );
}

Document.expandFlags = sectionsList;
Document.literatureFormatter = function (content) {
  return citationLiteratureFormatter(content);
};
Document.patentFormatter = function (content) {
  return patentFamilyFormatter(content);
};
Document.checkIfClickTargetContainsSelection = function (target) {
  const selection = window.getSelection();
  const isSelected = !!selection.toString();
  const selectedNode = getValueByNs(selection, 'anchorNode.parentNode');
  return isSelected && selectedNode && target.contains(selectedNode);
};

Document.propTypes = {
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  context: PropTypes.string,
};

export default Document;
